import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, fmt } from "../client";
import { PageHeader, Panel, Pill, Empty } from "../ui";

const TONE = { pending: "warn", approved: "ok", archived: "dim" };

const Card = ({ a, onAct }) => (
  <Panel className="s-rise flex flex-col gap-3 p-5" data-testid={`partner-card-${a.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div><p className="s-display text-lg">{a.name || "Unnamed"}</p><p className="text-xs text-[var(--s-dim)]">{a.type || "—"}</p></div>
      <Pill tone={TONE[a.status] || "dim"}>{a.status}</Pill>
    </div>
    <div className="space-y-1 text-sm">
      <p className="break-all">{a.email || "—"}</p>
      <p className="text-[var(--s-dim)]">{a.phone || "No phone"}</p>
    </div>
    <div className="flex flex-wrap gap-2"><Pill tone={a.corridor === "Other" ? "dim" : "ok"}>{a.corridor}{a.corridor === "Other" && a.region ? ` · ${a.region}` : ""}</Pill>{a.tier && <Pill>{a.tier}</Pill>}</div>
    {a.pitch && <p className="text-sm text-[var(--s-dim)]">{a.pitch}</p>}
    {a.referral_url && (
      <div className="rounded-lg border border-[var(--s-line)] p-3 text-xs" data-testid={`partner-provision-${a.id}`}>
        <p className="s-mono break-all text-[var(--s-accent)]">{a.referral_url}</p>
        <p className="s-mono mt-1 break-all text-[var(--s-dim)]">claim token: {a.claim_token}</p>
      </div>
    )}
    <p className="s-mono text-[10px] text-[var(--s-dim)]">received {fmt(a.created_at)}</p>
    <div className="mt-auto flex flex-wrap gap-2 pt-2">
      {a.status === "pending" && <Button size="sm" className="s-btn" onClick={() => onAct(a, "approve")} data-testid={`partner-approve-${a.id}`}>Approve &amp; provision</Button>}
      {a.email && <Button size="sm" variant="outline" className="s-ghost" asChild data-testid={`partner-email-${a.id}`}>
        <a href={`mailto:${a.email}?subject=${encodeURIComponent("Your partnership application")}`} onClick={() => api(`/staff/partnerships/${a.id}/email-logged`, { method: "POST" }).catch(() => {})}>Email applicant</a>
      </Button>}
      {a.status !== "archived" && <Button size="sm" variant="outline" className="s-danger" onClick={() => onAct(a, "archive")} data-testid={`partner-archive-${a.id}`}>Archive</Button>}
    </div>
  </Panel>
);

export default function Partnerships() {
  const [rows, setRows] = useState(null);
  const [view, setView] = useState("pending");
  const load = useCallback(() => { api("/staff/partnerships").then(setRows).catch((e) => toast.error(e.message)); }, []);
  useEffect(() => { load(); }, [load]);
  const onAct = async (a, action) => {
    try { await api(`/staff/partnerships/${a.id}/${action}`, { method: "POST" }); toast.success(action === "approve" ? "Approved & provisioned" : "Archived"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const list = (rows || []).filter((a) => a.status === view);
  return (
    <div>
      <PageHeader kicker="Venue & campus leads" title="Partnership pipeline">
        <Tabs value={view} onValueChange={setView}><TabsList>
          {["pending", "approved", "archived"].map((s) => <TabsTrigger key={s} value={s} className="capitalize" data-testid={`partners-tab-${s}`}>{s} {rows ? `(${rows.filter((a) => a.status === s).length})` : ""}</TabsTrigger>)}
        </TabsList></Tabs>
      </PageHeader>
      {rows === null ? <p className="text-sm text-[var(--s-dim)]">Loading…</p> : list.length === 0 ? <Empty>No applications in this stage.</Empty> : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="partners-grid">{list.map((a) => <Card key={a.id} a={a} onAct={onAct} />)}</div>
      )}
    </div>
  );
}
